/**
 * Basic slider event handling
 * [1. Navigation buttons] [2. Pagination buttons]
 */
class SliderBasicHandler {
  constructor(slider) {
    this.slider = slider;
    this.prevButton = this.slider.querySelector('button[name="previous"]');
    this.nextButton = this.slider.querySelector('button[name="next"]');
    this.pageDotsWrapper = this.slider.querySelector(".slider-page-dots"); // Pagination container
    this.counterWrapper = this.slider.querySelector(".slider-counter"); // Counter container
    this.sliderSeekBar = this.slider.querySelector(".slider-seek-bar"); // Seek bar

    this.initModule();
  }

  /**
   * Handle navigation button click
   * @param direction Direction: previous|next
   */
  onButtonClick(direction) {
    // Delegate to the slider's built-in slide method
    this.slider.slideByDirection(direction);
  }

  // Pagination dot click
  onPageLinkClick(event) {
    const dot = event.target.closest(".page-dot");
    if (!dot) return;
    const page = parseInt(dot.dataset.page, 10);
    this.slider.slideByPage(page);
  }

  initModule() {
    // Create and bind event handlers
    const onButtonClickFunction = webvista.debounce(
      this.onButtonClick.bind(this),
      200,
    );
    this.boundOnButtonClick = (event) => {
      const direction = event.currentTarget.name;
      onButtonClickFunction(direction);
    };

    // Calculate total page count
    this.pageCount = this.slider.slideLength;
    if (!this.slider.ifLoopingInfinite) {
      this.pageCount = this.slider.slideLength - this.slider.slidesPerPage + 1;
    }

    // Navigation buttons
    if (this.prevButton)
      this.prevButton.addEventListener("click", this.boundOnButtonClick);
    if (this.nextButton)
      this.nextButton.addEventListener("click", this.boundOnButtonClick);

    // Initialize pagination dots
    if (this.pageDotsWrapper) this.initPageDots();

    // Initialize page counter display
    if (this.counterWrapper) this.initCounter();

    // Initialize seek bar
    if (this.sliderSeekBar)
      this.seekBar = new SliderSeekBar(
        this.slider,
        this.sliderSeekBar,
        this.pageCount,
      );

    // Arrow key navigation support
    this.boundOnKeyDown = (event) => {
      const key = event.code ? event.code.toUpperCase() : "";
      if (key === "ARROWLEFT") {
        event.preventDefault();
        this.slider.slideByDirection("previous");
      }
      if (key === "ARROWRIGHT") {
        event.preventDefault();
        this.slider.slideByDirection("next");
      }
    };
    this.slider.addEventListener("keydown", this.boundOnKeyDown);
  }

  /**
   * Initialize pagination dots
   */
  initPageDots() {
    if (!this.pageDotsWrapper) return;
    this.boundOnPageButtonClick = this.onPageLinkClick.bind(this);
    this.boundOnPageDotsKeyDown = (event) => {
      const key = event.code ? event.code.toUpperCase() : "";
      if (key === "ENTER" || key === "SPACE") {
        event.preventDefault();
        this.boundOnPageButtonClick(event);
      }
    };

    // Event delegation on wrapper
    this.pageDotsWrapper.addEventListener("click", this.boundOnPageButtonClick);
    this.pageDotsWrapper.addEventListener("keydown", this.boundOnPageDotsKeyDown);

    for (let i = 1; i <= this.pageCount; i++) {
      const link = document.createElement("li");
      link.className = "page-dot";
      link.setAttribute("data-page", i);
      link.setAttribute("tabindex", "0"); // Make focusable
      if (this.pageDotsWrapper.hasAttribute("data-number"))
        link.innerText = i.toString();

      this.pageDotsWrapper.appendChild(link);
    }
  }

  /**
   * Initialize page counter display
   */
  initCounter() {
    const totalCounterElement =
      this.counterWrapper.querySelector(".total-page");
    if (totalCounterElement) totalCounterElement.innerText = this.pageCount;
  }

  /**
   * Update basic module info after page change
   * @param currentSlide Current slide element
   * @param totalPage Total page count
   */
  updateInfo(currentSlide = null, totalPage = 1) {
    if (!currentSlide) return;

    const currentPage = currentSlide.dataset.page;

    // Pagination dots
    if (this.pageDotsWrapper) {
      this.pageDotsWrapper
        .querySelector("li.current")
        ?.classList.remove("current");
      this.pageDotsWrapper
        .querySelector(`li[data-page="${currentPage}"]`)
        ?.classList.add("current");
    }

    // Current page counter
    if (this.counterWrapper) {
      const currentCounterElement =
        this.counterWrapper.querySelector(".current-page");
      if (currentCounterElement) {
        currentCounterElement.innerText = currentPage;
      }
    }

    // Navigation buttons
    if (!this.slider.ifEnableLooping) {
      this.prevButton &&
        this.prevButton.toggleAttribute("disabled", currentPage <= 1);
      this.nextButton &&
        this.nextButton.toggleAttribute("disabled", currentPage >= totalPage);
    }
  }

  /**
   * Unmount module
   */
  unmount() {
    if (this.prevButton)
      this.prevButton.removeEventListener("click", this.boundOnButtonClick);
    if (this.nextButton)
      this.nextButton.removeEventListener("click", this.boundOnButtonClick);

    // Unmount seek bar
    if (this.seekBar) this.seekBar.unmount();

    if (this.pageDotsWrapper) {
      this.pageDotsWrapper.removeEventListener("click", this.boundOnPageButtonClick);
      this.pageDotsWrapper.removeEventListener("keydown", this.boundOnPageDotsKeyDown);
      this.pageDotsWrapper.innerHTML = "";
    }

    if (this.boundOnKeyDown)
      this.slider.removeEventListener("keydown", this.boundOnKeyDown);
  }
}

/**
 * SliderAutoplay manages the autoplay functionality for the slider.
 * It starts or pauses playback based on slider visibility and user interactions
 * (e.g., mouse hover or focus events). Uses IntersectionObserver API to detect
 * whether the slider is visible in the viewport.
 */
class SliderAutoplay {
  /**
   * Constructor: initialize the autoplay class.
   * @param {Slider} slider - Reference to the slider.
   * @param {number} interval - Autoplay interval in milliseconds.
   * @param {boolean} reverse - Whether to play in reverse direction, default: false
   * @param focusPause - Whether to pause playback on focus
   * @param {number} autoplayDelay - Delay before the first autoplay transition
   */
  constructor(slider, interval, reverse = false, focusPause = false, autoplayDelay = 0) {
    this.slider = slider;
    this.interval = parseInt(interval, 10) || 0;
    this.autoplayDelay = parseInt(autoplayDelay, 10) || 0;
    this.useAutoplayDelay = this.autoplayDelay > 0;
    this.autoInterval = null;
    this.direction = reverse ? "previous" : "next";
    this.focusPause = focusPause;

    this.toggleAutoplayButton = this.slider.querySelector(
      ".slider-toggle-autoplay-button",
    );

    this.ifPaused = false; // Whether playback is paused
    this.userPaused = false; // Whether the user manually paused playback
    this.startTime = null; // Start time of the current cycle
    this.elapsedTime = null; // Elapsed time when paused, used for resuming
    this.leftTime = this.useAutoplayDelay ? this.autoplayDelay : this.interval; // Remaining time for the next cycle

    this.initMount();
  }

  /**
   * Play
   * @param refresh Whether to reset the timer
   */
  play(refresh = false) {
    if (refresh) {
      if (this.autoInterval) clearTimeout(this.autoInterval);
      this.startTime = null;
      this.leftTime = this.useAutoplayDelay ? this.autoplayDelay : this.interval;
      this.useAutoplayDelay = false;
    }
    this.ifPaused = false;
    this.slider.classList.add("slider--is-playing"); // Used for pause/resume control
    this.#doPlay();
  }

  /**
   * Execute playback
   */
  #doPlay() {
    if (this.slider.hasAttribute("editor-selected") || this.ifPaused) return;

    this.startTime = Date.now(); // Record the cycle start time
    this.autoInterval = setTimeout(() => {
      this.slider.slideByDirection(this.direction);

      this.leftTime = this.interval; // Required to prevent unhandled slideChange in very short intervals
    }, this.leftTime);
  }

  /**
   * Pause playback
   */
  pause() {
    if (!this.autoInterval) return;
    this.ifPaused = true;
    clearTimeout(this.autoInterval);
    this.autoInterval = null;

    // If playback has started, calculate the elapsed time for the current cycle.
    // Then compute the remaining time for resuming playback
    if (this.startTime) {
      this.elapsedTime = Date.now() - this.startTime;
      this.leftTime = Math.max(
        0,
        Math.min(this.interval, this.leftTime - this.elapsedTime),
      );
    }

    this.slider.classList.remove("slider--is-playing"); // Pause playback
  }

  /**
   * Toggle between pause and resume via button
   */
  toggleAutoplayHandling() {
    if (this.ifPaused) {
      this.userPaused = false;
      this.play();
    } else {
      this.userPaused = true;
      this.pause();
    }
  }

  /**
   * Triggered on focus or mouse hover to pause autoplay.
   */
  focusInHandling() {
    this.pause(); // Pause autoplay
  }

  /**
   * Triggered on blur or mouse leave to resume autoplay.
   */
  focusOutHandling() {
    if (!this.userPaused) this.play(); // Resume autoplay only if not manually paused
  }

  /**
   * Add event listeners for focus and mouse events.
   */
  initMount() {
    // Bind event handlers
    if (this.focusPause) {
      this.boundFocusInHandling = this.focusInHandling.bind(this);
      this.boundFocusOutHandling = this.focusOutHandling.bind(this);
      this.slider.addEventListener("mouseover", this.boundFocusInHandling);
      this.slider.addEventListener("mouseleave", this.boundFocusOutHandling);
      this.slider.addEventListener("focusin", this.boundFocusInHandling);
      this.slider.addEventListener("focusout", this.boundFocusOutHandling);
    }

    if (this.toggleAutoplayButton) {
      this.boundToggleHandling = this.toggleAutoplayHandling.bind(this);
      this.toggleAutoplayButton.addEventListener(
        "click",
        this.boundToggleHandling,
      );
    }
  }

  /**
   * Unmount module.
   */
  unmount() {
    if (this.focusPause) {
      this.slider.removeEventListener("mouseover", this.boundFocusInHandling);
      this.slider.removeEventListener("mouseleave", this.boundFocusOutHandling);
      this.slider.removeEventListener("focusin", this.boundFocusInHandling);
      this.slider.removeEventListener("focusout", this.boundFocusOutHandling);
    }

    if (this.toggleAutoplayButton)
      this.toggleAutoplayButton.removeEventListener(
        "click",
        this.boundToggleHandling,
      );
  }
}

/**
 * SliderDrag handles drag functionality for the slider.
 * Allows users to switch slides via mouse or touch dragging.
 */
class SliderDrag {
  /**
   * Constructor: initialize drag functionality.
   * @param {Slider} slider - Reference to the slider.
   * @param {Boolean} smooth - Whether to enable smooth scrolling; if false, drag has no animation
   */
  constructor(slider, smooth = true) {
    this.DRAG_MOVE_RATE = 1.5; // Drag movement rate multiplier

    this.slider = slider;
    this.smooth = smooth;

    this.isClicking = false; // Whether a click is in progress
    this.isDragging = false; // Whether dragging is in progress
    this.isTouch = "ontouchstart" in window;
    this.clickThreshold = this.isTouch ? 8 : 5; // Adjustable as needed
    this.noiseThreshold = 5; // Single-frame noise threshold to avoid micro-jitter
    this.suppressClickUntil = 0; // Timestamp until which clicks are suppressed

    this.boundDragStart = this.onDragStart.bind(this);
    this.boundDragMove = this.onDragMove.bind(this);
    this.boundDragEnd = this.onDragEnd.bind(this);

    // Bind event listeners
    this.bindEventListener();
  }

  /**
   * Normalize mouse/touch event to get the primary axis coordinate
   * @param {MouseEvent|TouchEvent} e
   * @returns {number}
   */
  getAxisValue(e) {
    if (e.touches) {
      return this.slider.ifLayoutVertical
        ? e.touches[0].clientY
        : e.touches[0].clientX;
    }
    return this.slider.ifLayoutVertical ? e.clientY : e.clientX;
  }

  /**
   * Handle drag start event.
   * @param {MouseEvent|TouchEvent} event - The event that triggered the drag.
   */
  onDragStart(event) {
    this.isClicking = true;
    this.isDragging = false;

    this.dragStartPos = this.getAxisValue(event);
    this.dragLastPos = this.dragStartPos;
    this.totalDragDistance = 0; // Reset cumulative displacement

    this.slider.disableTransition();

    // Bind global events
    document.addEventListener("mousemove", this.boundDragMove);
    document.addEventListener("mouseup", this.boundDragEnd);
    document.addEventListener("touchmove", this.boundDragMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.boundDragEnd);
  }

  /**
   * Handle drag move event.
   * @param {MouseEvent|TouchEvent} event - The event that triggered the move.
   */
  onDragMove(event) {
    if (!this.isClicking) return;

    const currentPos = this.getAxisValue(event);
    let delta = this.dragLastPos - currentPos;
    this.dragLastPos = currentPos;

    // RTL only affects direction, not click/drag detection
    if (!this.slider.ifLayoutVertical && this.slider.isRTL) {
      delta *= -1; // Ensure consistent displacement direction
    }
    const absDelta = Math.abs(delta);

    if (absDelta > this.noiseThreshold) {
      this.totalDragDistance += absDelta;

      // Once determined as drag, prevent touch default to avoid page scrolling
      if (event.touches && event.cancelable) event.preventDefault();

      if (!this.isDragging) {
        this.isDragging = true;
        this.slider.classList.add("slider--is-dragging");
      }

      // Use rAF for smoother animation
      this.rafId = requestAnimationFrame(() => {
        this.slider.slideByOffset(delta * this.DRAG_MOVE_RATE, !this.smooth);
      });
    }
  }

  /**
   * Handle drag end event.
   * @param {MouseEvent|TouchEvent} event - The event that triggered drag end.
   */
  onDragEnd(event) {
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);

    this.isClicking = false;
    this.slider.enableTransition(); // Restore transition animation

    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.slider.classList.remove("slider--is-dragging");

    // Determined as "click": pass through, no snapping or page change
    if (!this.isDragging && this.totalDragDistance <= this.clickThreshold) {
      return;
    }

    // Determined as "drag": snap to position and briefly suppress click
    this.isDragging = false;
    this.suppressClickUntil = Date.now() + 120; // Suppress click for 120ms

    const dragDelta = this.dragLastPos - this.dragStartPos;
    let moveDirection;
    if (!this.slider.ifLayoutVertical && this.slider.isRTL) {
      moveDirection = dragDelta > 0 ? "to-previous" : "to-next";
    } else {
      moveDirection = dragDelta > 0 ? "to-next" : "to-previous";
    }

    this.slider.snapAfterOffset(moveDirection);
  }

  /**
   * Bind drag event listeners to the slider.
   */
  bindEventListener() {
    // Prevent native drag behavior (especially for IMG elements)
    this.boundPreventNativeDrag = (event) => {
      if (
        event.target &&
        (event.target.tagName === "IMG" || event.target.closest("img"))
      ) {
        event.preventDefault();
      }
    };
    this.slider.sliderContainer.addEventListener(
      "dragstart",
      this.boundPreventNativeDrag,
      true,
    );

    // Prevent text/image selection during drag to avoid blue selection box
    this.boundPreventSelect = (event) => {
      if (this.isDragging) event.preventDefault();
    };
    this.slider.sliderContainer.addEventListener(
      "selectstart",
      this.boundPreventSelect,
      true,
    );

    this.slider.sliderContainer.addEventListener(
      "mousedown",
      this.boundDragStart,
      { passive: true },
    );
    this.slider.sliderContainer.addEventListener(
      "touchstart",
      this.boundDragStart,
      { passive: true },
    );

    // Intercept click in capture phase
    this.boundClickCapture = (event) => {
      if (Date.now() < this.suppressClickUntil) {
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        event.preventDefault();
      }
    };
    this.slider.sliderContainer.addEventListener(
      "click",
      this.boundClickCapture,
      true,
    );
  }

  /**
   * Unmount module.
   */
  unmount() {
    // Remove mouse events
    this.slider.sliderContainer.removeEventListener(
      "mousedown",
      this.boundDragStart,
    );
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);

    this.slider.sliderContainer.removeEventListener(
      "touchstart",
      this.boundDragStart,
    );
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);

    if (this.boundPreventNativeDrag)
      this.slider.sliderContainer.removeEventListener(
        "dragstart",
        this.boundPreventNativeDrag,
        true,
      );
    if (this.boundPreventSelect)
      this.slider.sliderContainer.removeEventListener(
        "selectstart",
        this.boundPreventSelect,
        true,
      );
    if (this.boundClickCapture)
      this.slider.sliderContainer.removeEventListener(
        "click",
        this.boundClickCapture,
        true,
      );
  }
}

/**
 * SliderWheel handles wheel/trackpad events for the slider.
 * Gesture-driven: content follows the finger during swipe, then snaps to the nearest slide.
 * Supports Mac trackpad, mouse wheel, and horizontal/vertical layouts.
 */
class SliderWheel {
  /**
   * Constructor: initialize wheel functionality.
   * @param {Slider} slider - Reference to the slider.
   */
  constructor(slider) {
    this.slider = slider;
    this.GESTURE_TIMEOUT = 100; // ms gap to detect gesture end

    this.isGesturing = false;
    this.gestureStartTranslate = 0;
    this.gestureEndTimer = null;

    this.boundHandleWheel = this.handleWheel.bind(this);
    this.bindEventListener();
  }

  /**
   * Handle wheel event �?continuous gesture tracking
   * @param {WheelEvent} event
   */
  handleWheel(event) {
    const isVertical = this.slider.ifLayoutVertical;
    let primaryDelta = isVertical ? event.deltaY : event.deltaX;
    const crossDelta = isVertical ? event.deltaX : event.deltaY;

    // Normalize deltaMode: 1 = lines (~16px), 2 = pages
    if (event.deltaMode === 1) primaryDelta *= 16;
    else if (event.deltaMode === 2) primaryDelta *= this.slider.sliderClientSize;

    if (!this.isGesturing) {
      // Axis detection: if cross-axis is dominant, let page scroll normally
      if (Math.abs(crossDelta) > Math.abs(primaryDelta)) return;
      if (Math.abs(primaryDelta) < 3) return;

      // Boundary check: don't start gesture if already at the edge
      const canNext = this.slider.ifEnableLooping || this.slider.index < this.slider.totalPages - 1;
      const canPrev = this.slider.ifEnableLooping || this.slider.index > 0;
      if ((primaryDelta > 0 && !canNext) || (primaryDelta < 0 && !canPrev)) return;

      this.isGesturing = true;
      this.gestureStartTranslate = this.slider.currentTranslate;
      this.slider.disableTransition();
    }

    event.preventDefault();
    event.stopPropagation();

    // RTL flip for horizontal layout (mirrors SliderDrag behavior)
    let offset = primaryDelta;
    if (!isVertical && this.slider.isRTL) offset *= -1;

    this.slider.slideByOffset(offset);

    // Reset gesture end timer �?includes momentum/inertia events
    clearTimeout(this.gestureEndTimer);
    this.gestureEndTimer = setTimeout(() => this.onGestureEnd(), this.GESTURE_TIMEOUT);
  }

  /**
   * Gesture ended �?snap to nearest slide
   */
  onGestureEnd() {
    if (!this.isGesturing) return;
    this.isGesturing = false;
    this.slider.enableTransition();

    const translateDelta = this.slider.currentTranslate - this.gestureStartTranslate;
    if (Math.abs(translateDelta) < 1) return; // No meaningful movement

    // Direction matches snapAfterOffset convention:
    // translate increased �?content shifted backward �?snap with ceil (to-previous)
    // translate decreased �?content shifted forward  �?snap with floor (to-next)
    const direction = translateDelta > 0 ? "to-previous" : "to-next";
    this.slider.snapAfterOffset(direction);
  }

  bindEventListener() {
    this.slider.addEventListener("wheel", this.boundHandleWheel, {
      passive: false, // Required for preventDefault()
    });
  }

  /**
   * Unmount module
   */
  unmount() {
    clearTimeout(this.gestureEndTimer);
    this.slider.removeEventListener("wheel", this.boundHandleWheel);
  }
}

/**
 * SliderInfiniteLoop handles seamless infinite loop scrolling mode.
 * Supports infinite dragging.
 */
class SliderInfiniteLoop {
  constructor(slider) {
    this.slider = slider;
    this.paddingSlides();

    this.boundOnSlideChange = this.onSlideChange.bind(this);
    this.slider.addEventListener("sliderChanged", this.boundOnSlideChange);
  }

  /**
   * Pad slides with clones before and after the original slides.
   * The number of clones depends on visible slides per page: 2n-1 clones needed before and after,
   * where n is the number of simultaneously visible items.
   * E.g., if 3 items visible, 5 clones are prepended and 5 appended.
   */
  paddingSlides() {
    const {
      slidesPerPage,
      slideLength: totalSlides,
    } = this.slider;
    const paddingCount = 2 * slidesPerPage - 1;

    // Create document fragments
    const fragmentAfter = document.createDocumentFragment();
    const fragmentBefore = document.createDocumentFragment();
    for (let i = 0; i < paddingCount; i++) {
      // Clone slides and append to document fragments
      let cloneSlideItem = this.cloneSlide(i % totalSlides, `after-${i}`);
      if (cloneSlideItem) fragmentAfter.append(cloneSlideItem);

      cloneSlideItem = this.cloneSlide(totalSlides - 1 - (i % totalSlides), `before-${i}`);
      if (cloneSlideItem) fragmentBefore.prepend(cloneSlideItem);
    }
    // Append and prepend clone slides
    this.slider.sliderWrapper.append(fragmentAfter);
    this.slider.sliderWrapper.prepend(fragmentBefore);

    this.slider.updateSliderMetrics(); // Update slider metrics
    this.slider.index = paddingCount; // Reposition to account for prepended clones
    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(); // Reinitialize scroll animation observers
    }

    webvista.initLazyImages(); // Reinitialize lazy image loading
    webvista.initToolTips();
  }

  /**
   * Clone a slide item
   * @param fromIndex
   * @returns {ActiveX.IXMLDOMNode|Node|boolean}
   */
  cloneSlide(fromIndex = null, suffix = "0") {
    const cloneSlide = this.slider.sliderItems[fromIndex].cloneNode(true);
    if (!cloneSlide) return false;

    cloneSlide.classList.add("slider-slide--clone");
    cloneSlide.setAttribute("aria-hidden", true); // Hide from assistive devices
    cloneSlide.setAttribute("data-clone-from", `#${cloneSlide.id}`);
    cloneSlide.setAttribute("data-page", fromIndex + 1);
    cloneSlide.id = `${cloneSlide.id}-Clone-${suffix}`;

    // Remove Shopify editor attributes
    cloneSlide.removeAttribute("data-shopify-editor-block");

    // Remove tabindex to prevent focus on clones (focus should stay on original items)
    cloneSlide.removeAttribute("tabindex");
    const elements = webvista.getFocusableElements(cloneSlide, false);

    if (elements && elements.length > 0) {
      elements.forEach((element) => {
        element.setAttribute("tabindex", -1);
      });
    }

    return cloneSlide;
  }

  /**
   * Remove padded clone slides
   */
  removePaddings() {
    this.slider
      .querySelectorAll(".slider-slide--clone")
      .forEach((element) => element.remove());
  }

  /**
   * Handle seamless loop transition
   * @param event
   */
  onSlideChange(event) {
    if (event.target !== this.slider) return; // For nested sliders, only handle events from the current slider

    const currentSlide = event.detail.currentElement;
    if (currentSlide && currentSlide.hasAttribute("data-clone-from")) {
      const cloneFromId = currentSlide.dataset.cloneFrom;

      if (cloneFromId)
        setTimeout(() => {
          // Delay to prevent white flash
          this.slider.slideById(cloneFromId, true);
        }, 30);
    }
  }

  /**
   * Unmount module
   */
  unmount() {
    this.slider.removeEventListener("sliderChanged", this.boundOnSlideChange);
    this.removePaddings();
  }
}

/**
 * Slider progress seek bar
 * Draggable to switch slides
 */
class SliderSeekBar {
  constructor(slider, element, pageCount) {
    this.slider = slider;
    this.element = element;
    this.pageCount = pageCount;

    this.init();
  }

  /**
   * Initialize
   */
  init() {
    const rect = this.element.getBoundingClientRect();
    if (rect.width <= 0) return; // Guard against zero-width element

    const width = rect.width / this.pageCount; // Handle width
    const progressWidth = rect.width - width; // Progress width = total width minus handle width
    if (progressWidth <= 0) return;

    // Ratio between seek bar movement and slider movement
    this.ratio = this.slider.maxTranslateValue / progressWidth;

    this.element.style.setProperty("--handle-width", `${width}px`);
    this.element.style.setProperty("--progress-width", `${progressWidth}px`);

    this.boundDragStart = this.onDragStart.bind(this);
    this.boundDragMove = this.onDragMove.bind(this);
    this.boundDragEnd = this.onDragEnd.bind(this);

    this.element.addEventListener("mousedown", this.boundDragStart);
    this.element.addEventListener("touchstart", this.boundDragStart, {
      passive: false,
    });
  }

  onDragStart(event) {
    event.preventDefault();
    this.isClicking = true;
    this.isDragging = false;
    this.clickThreshold = 5;
    this.totalDragDistance = 0;

    this.dragStartPos = event.touches ? event.touches[0].clientX : event.clientX;
    this.dragCurrentPos = this.dragStartPos;

    document.addEventListener("mousemove", this.boundDragMove);
    document.addEventListener("mouseup", this.boundDragEnd);
    document.addEventListener("touchmove", this.boundDragMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.boundDragEnd);
  }

  onDragMove(event) {
    if (!this.isClicking) return;
    const currentPos = event.touches ? event.touches[0].clientX : event.clientX;
    const dragOffset = currentPos - this.dragCurrentPos;
    this.dragCurrentPos = currentPos;

    if (Math.abs(dragOffset) > 1) {
      this.totalDragDistance += Math.abs(dragOffset);
      event.preventDefault();
      this.isDragging = true;
      this.element.classList.add("is-dragging");
      this.slider.slideByOffset(dragOffset * this.ratio);
    }
  }

  /**
   * Drag end
   * @param event
   */
  onDragEnd(event) {
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);

    this.isClicking = false;
    if (!this.isDragging && this.totalDragDistance <= this.clickThreshold)
      return;

    this.isDragging = false;
    this.element.classList.remove("is-dragging");

    const dragDelta = this.dragStartPos - this.dragCurrentPos;
    let moveDirection;
    if (!this.slider.ifLayoutVertical && this.slider.isRTL) {
      moveDirection = dragDelta > 0 ? "to-previous" : "to-next";
    } else {
      moveDirection = dragDelta > 0 ? "to-next" : "to-previous";
    }

    this.slider.snapAfterOffset(moveDirection);
  }

  unmount() {
    this.element.removeEventListener("mousedown", this.boundDragStart);
    this.element.removeEventListener("touchstart", this.boundDragStart);
    document.removeEventListener("mousemove", this.boundDragMove);
    document.removeEventListener("mouseup", this.boundDragEnd);
    document.removeEventListener("touchmove", this.boundDragMove);
    document.removeEventListener("touchend", this.boundDragEnd);
  }
}

/**
 * Base slider class
 * Supports [autoplay] [looping] [infinite loop] [draggable] [wheelable] [vertical layout]
 * Lifecycle hooks for subclass overrides: hookBeforeInstall, hookAfterInstall
 */
class Slider extends HTMLElement {
  constructor() {
    super();
    // Configuration constants
    this.TRANSITION_DURATION = 300; // Transition animation duration in ms
    this.DEFAULT_INTERVAL = 5000; // Autoplay interval in ms
    this.TRANSITION_THRESHOLD = 50; // Boundary threshold for drag overshoot

    // State parameters
    this.index = 0; // Current slide index
    this.initSliderStatus = false; // Whether slider initialization is complete
    this.preTranslate = 0; // Previous translate position, used during drag
    this.currentTranslate = 0; // Current translate position

    this.lastWindowSize = window.innerWidth;

    this.sliderContainer = this.querySelector(".slider-container");
    this.sliderWrapper = this.querySelector(".slider-wrapper");
    if (!this.sliderContainer || !this.sliderWrapper) return;

    // Initialize base configuration
    this.initConfig();

    // Add window resize listener
    this.debounceWindowSizeChangeHandler = webvista.debounce(
      this.onWindowSizeChange.bind(this),
      500,
    );
    window.addEventListener("resize", this.debounceWindowSizeChangeHandler);

    const observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.install();
            obs.unobserve(this);
            break;
          }
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );

    observer.observe(this);
  }

  disconnectedCallback() {
    this.uninstall();

    // Remove window resize listener
    window.removeEventListener(
      "resize",
      this.debounceWindowSizeChangeHandler,
    );
  }

  // Install
  install() {
    if (this.initSliderStatus) return;

    // Check screen size to determine if slider should be enabled
    if (webvista.isMobileScreen() && !this.ifSlideMobile) {
      return;
    } else if (!webvista.isMobileScreen() && !this.ifSlideDesktop) {
      return;
    }

    this.hookBeforeInstall(); // Lifecycle hook: execute before install

    // Initialize position, default is 0. Note: subsequent modules may modify this value!
    this.index = 0;

    // Update slide metrics and related data
    this.updateSliderMetrics();

    // Verify slider conditions before mounting modules
    if (!this.verifyBeforeMountModules()) return;

    // Assign page numbers to slide elements
    this.initPages();

    // Mount feature modules
    this.mountModules();

    // Initialize slider position
    this.initPosition();

    // Complete slider initialization
    this.toggleStatus(true);

    this.hookAfterInstall(); // Lifecycle hook: execute after install
  }

  hookBeforeInstall() {}

  hookAfterInstall() {}

  // Uninstall
  uninstall() {
    if (this.autoplayHandler) this.autoplayHandler.pause(); // Stop playback

    // Unmount feature modules
    this.unmountModules();

    // Clear slider translation
    // Required: without this, switching from desktop (slider layout) to mobile (grid layout)
    // would cause layout anomalies
    this.clearTranslation();

    // Reset slider initialization state
    this.toggleStatus(false);
  }

  // Reinstall
  reInstall() {
    if (this.initSliderStatus) this.uninstall();
    this.install();
  }

  /**
   * Initialize configuration from data attributes
   * data-slide-desktop {Boolean} Enable slider on desktop
   * data-slide-mobile {Boolean} Enable slider on mobile
   * data-autoplay {Boolean} Enable autoplay
   * data-autoplay-reverse {Boolean} Reverse autoplay direction, requires autoplay enabled
   * data-interval {Int} Autoplay interval in ms
   * data-autoplay-delay {Int} Delay before the first autoplay transition in ms
   * data-focus-pause {Boolean} Pause playback on focus
   * data-slide-smooth {Boolean} Enable smooth scroll animation
   * data-draggable {Boolean} Enable drag support
   * data-wheelable {Boolean} Enable mouse wheel support
   * data-looping-infinite {Boolean} Enable infinite seamless loop
   * data-looping {Boolean} Enable looping, auto-enabled when infinite loop or autoplay is on
   * data-keep-video Keep video media playback state instead of pausing/playing on slide change
   */
  initConfig() {
    this.ifSlideDesktop = this.hasAttribute("data-slide-desktop"); // Enable slider on desktop
    this.ifSlideMobile = this.hasAttribute("data-slide-mobile"); // Enable slider on mobile
    this.ifEnableAutoplay = this.hasAttribute("data-autoplay"); // Enable autoplay, default: false
    this.ifReversePlay = this.hasAttribute("data-autoplay-reverse"); // Reverse autoplay direction
    this.interval = this.dataset.interval || this.DEFAULT_INTERVAL; // Autoplay interval in ms
    this.autoplayDelay = this.dataset.autoplayDelay || 0; // First autoplay delay in ms
    this.ifFocusPause = this.hasAttribute("data-focus-pause"); // Pause on focus

    // Enable smooth scrolling, default: false; recommended when infinite loop is enabled
    this.ifSlideSmooth = this.hasAttribute("data-slide-smooth");
    this.ifEnableDragging = this.hasAttribute("data-draggable"); // Enable drag, default: false
    this.ifEnableWheeling = this.hasAttribute("data-wheelable"); // Enable wheel, default: false
    this.ifLoopingInfinite = this.hasAttribute("data-looping-infinite"); // Enable infinite loop, default: false
    this.ifEnableLooping =
      this.ifLoopingInfinite ||
      this.ifEnableAutoplay ||
      this.hasAttribute("data-looping"); // Enable looping, default: false

    // Accessibility
    this.ifAriaLive = this.hasAttribute("aria-live");

    // Video media playback
    this.keepVideo = this.hasAttribute("data-keep-video");

    // Cache RTL state (does not change during page lifecycle)
    this.isRTL = webvista.isRTL();
  }

  /**
   * Initialize page numbers on slide items
   */
  initPages() {
    this.sliderItems.forEach((sliderItem, index) => {
      sliderItem.setAttribute("data-page", index + 1);
    });
  }

  /**
   * Initialize slider position
   */
  initPosition() {
    this.disableTransition();
    this.performSlide();

    setTimeout(() => {
      this.enableTransition();
    }, this.TRANSITION_DURATION);
  }

  /**
   * Verify whether slider installation conditions are met
   * @returns {boolean}
   */
  verifyBeforeMountModules() {
    return this.sliderTotalSize > this.sliderClientSize && this.totalPages > 1;
  }

  /**
   * Handle window resize event
   * Tracks both width and height changes
   */
  onWindowSizeChange() {
    const currentSize = this.ifLayoutVertical ? window.innerHeight : window.innerWidth;

    if (currentSize !== this.lastWindowSize) {
      this.reInstall();

      this.lastWindowSize = currentSize;
    }
  }

  /**
   * Toggle slider installation status
   * @param status
   */
  toggleStatus(status = false) {
    this.initSliderStatus = status;
    this.classList.toggle("slider--installed", status);

    // Add/remove layout direction classes
    if (status) {
      this.sliderContainer.classList.toggle(
        "is-horizontal",
        !this.ifLayoutVertical,
      );
      this.sliderContainer.classList.toggle(
        "is-vertical",
        this.ifLayoutVertical,
      );
    } else {
      this.sliderContainer.classList.remove("is-horizontal", "is-vertical");
    }
  }

  /**
   * Mount all modules
   */
  mountModules() {
    if (this.initSliderStatus) return;

    // Basic event handler including navigation and pagination buttons
    this.basicHandler = new SliderBasicHandler(this);

    // Initialize drag handler
    if (this.ifEnableDragging)
      this.dragHandler = new SliderDrag(this, this.ifSlideSmooth);

    // Initialize wheel handler
    if (this.ifEnableWheeling && !webvista.isMobileScreen())
      this.wheelHandler = new SliderWheel(this);

    // Infinite loop handler
    if (this.ifLoopingInfinite)
      this.seamLoopingHandler = new SliderInfiniteLoop(this);

    // Autoplay handler
    if (this.ifEnableAutoplay)
      this.autoplayHandler = new SliderAutoplay(
        this,
        this.interval,
        this.ifReversePlay,
        this.ifFocusPause,
        this.autoplayDelay,
      );
  }

  /**
   * Unmount all modules
   */
  unmountModules() {
    // Remove basic event handler including navigation and pagination buttons
    if (this.basicHandler) this.basicHandler.unmount();

    if (this.ifEnableDragging && this.dragHandler) this.dragHandler.unmount();

    if (this.ifEnableWheeling && this.wheelHandler) this.wheelHandler.unmount();

    if (this.ifEnableAutoplay && this.autoplayHandler)
      this.autoplayHandler.unmount();

    if (this.ifLoopingInfinite && this.seamLoopingHandler)
      this.seamLoopingHandler.unmount();

    if (Shopify.designMode && this.shopifyDesignModelHandler)
      this.shopifyDesignModelHandler.unmount();
  }

  /**
   * Update slide metrics and related static data.
   * Includes: total slide count, individual slide width, viewport width, total content width, visible items per page, etc.
   */
  updateSliderMetrics() {
    this.sliderItems = Array.from(
      this.sliderContainer.querySelectorAll('[id^="Slide-"]'),
    ).filter(
      (slide) => slide.children.length > 0 && slide.offsetParent !== null,
    );

    this.slideLength = this.sliderItems.length;
    if (this.slideLength <= 1) return;

    // Get size and count data
    // getBoundingClientRect provides higher precision than offsetLeft
    const sliderContainerRect = this.sliderContainer.getBoundingClientRect();
    const sliderRect0 = this.sliderItems[0].getBoundingClientRect();
    const sliderRect1 = this.sliderItems[1].getBoundingClientRect();
    const sliderRectLast = this.sliderItems.at(-1).getBoundingClientRect();

    // Auto-detect horizontal vs vertical layout
    this.ifLayoutVertical = false;
    if (sliderRect1.top > sliderRect0.top) {
      this.sliderItemOffset = sliderRect1.top - sliderRect0.top;
      this.sliderClientSize = sliderContainerRect.height;
      this.sliderTotalSize = sliderRectLast.bottom - sliderRect0.top;

      this.ifLayoutVertical = true;
    } else {
      this.sliderClientSize = sliderContainerRect.width;
      if (this.isRTL) {
        // RTL layout handling
        this.sliderItemOffset = sliderRect0.left - sliderRect1.left;
        this.sliderTotalSize = sliderRect0.right - sliderRectLast.left;
      } else {
        this.sliderItemOffset = sliderRect1.left - sliderRect0.left;
        this.sliderTotalSize = sliderRectLast.right - sliderRect0.left;
      }
    }
    if (this.sliderTotalSize <= this.sliderClientSize) return;

    this.slidesPerPage = Math.max(
      1,
      Math.round(this.sliderClientSize / this.sliderItemOffset),
    );
    if (this.slideLength === this.slidesPerPage) this.slidesPerPage--; // Prevent single-page scenario where the last item is clipped; force two pages
    this.maxTranslateValue = this.sliderTotalSize - this.sliderClientSize;
    this.totalPages = this.slideLength - this.slidesPerPage + 1;
  }

  // Slide by direction
  slideByDirection(direction = "next") {
    if (!this.initSliderStatus) return;
    this.index += direction === "previous" ? -1 : 1;

    this.performSlide();
  }

  /**
   * Slide to a specific element's position
   * @param element
   * @param silent Whether to skip transition animation
   * @returns {boolean}
   */
  slideByElement(element = null, silent = false) {
    if (!this.initSliderStatus || !element) return false;

    // If element is a clone, find the original element
    if (element.hasAttribute("data-clone-from"))
      element = this.querySelector(element.getAttribute("data-clone-from"));
    if (!element) return false;

    const elementIndex = this.sliderItems.indexOf(element);
    if (elementIndex < 0) return false;

    this.index = elementIndex;
    if (silent) this.disableTransition(); // Disable animation
    this.performSlide();
    if (silent)
      setTimeout(this.enableTransition.bind(this), this.TRANSITION_DURATION); // Re-enable animation
  }

  /**
   * Slide to a specific page
   * @param page Page number
   * @param silent Whether to skip transition animation
   */
  slideByPage(page = 1, silent = false) {
    if (!this.initSliderStatus || page < 1) return false;

    const slide = this.sliderItems.find(
      (slide) =>
        !slide.hasAttribute("data-clone-from") &&
        parseInt(slide.dataset.page, 10) === page,
    );
    this.slideByElement(slide, silent);
  }

  /**
   * Slide to an element by ID (with #, e.g., #Slide-1)
   * @param id
   * @param silent Whether to skip transition animation
   */
  slideById(id, silent = false) {
    if (!this.initSliderStatus || !id) return false;

    const slide = this.querySelector(id);
    if (!slide) return false;

    this.slideByElement(slide, silent);
  }

  /**
   * Slide by a given offset distance
   * @param offset
   * @param silence Whether silent mode (no visual movement)
   */
  slideByOffset(offset = 0, silent = false) {
    this.preTranslate = this.currentTranslate;
    this.currentTranslate += offset;

    if (!silent) this.applyTranslation();
  }

  /**
   * After offset, snap to the nearest slide item
   * @param direction Movement direction. to-previous: right-to-left or upward; to-next: left-to-right or downward
   */
  snapAfterOffset(direction) {
    const DRAG_THRESHOLD = Math.min(
      this.sliderItemOffset / 2,
      this.TRANSITION_THRESHOLD - 1, // -1 to allow triggering from the start
    );

    if (direction === "to-next") {
      // Moving left-to-right or downward
      this.index = Math.floor(
        (this.currentTranslate + DRAG_THRESHOLD) / this.sliderItemOffset,
      );
    } else {
      // Moving right-to-left or upward
      this.index = Math.ceil(
        (this.currentTranslate - DRAG_THRESHOLD) / this.sliderItemOffset,
      );
    }

    this.performSlide();
  }

  /**
   * Scroll a slide item into the visible area
   * @param index
   */
  slideToVisible(index) {
    const slide = this.sliderItems[index];
    if (!slide) return;

    if (this.isVisibleSlide(slide)) return;

    const slideRect = slide.getBoundingClientRect();
    const containerRect = this.sliderContainer.getBoundingClientRect();

    let offset;

    if (slideRect.right >= containerRect.right) {
      offset = slideRect.right - containerRect.right;
    } else {
      offset = slideRect.left - containerRect.left;
    }

    this.slideByOffset(offset);
  }

  /**
   * Execute slide transition
   */
  performSlide() {
    this.correctIndex(); // Correct index bounds

    this.updateCurrentTranslate();
    this.applyTranslation();

    this.updateInfo(); // Update display info
  }

  /**
   * Update current translate value
   */
  updateCurrentTranslate() {
    this.currentTranslate = this.index * this.sliderItemOffset;
    if (this.ifLoopingInfinite) return;

    // Handle case where viewport doesn't contain an exact integer number of slides
    if (this.index <= 0) {
      this.currentTranslate = 0;
    } else if (this.index >= this.totalPages - 1) {
      this.currentTranslate = this.maxTranslateValue;
    }
  }

  /**
   * Apply translate transform
   * Can be used standalone without changing the index
   */
  applyTranslation() {
    this.correctTranslate();
    this.sliderWrapper.style.transform = this.ifLayoutVertical
      ? `translate3d(0, ${-this.currentTranslate}px, 0)`
      : this.isRTL
        ? `translate3d(${this.currentTranslate}px, 0, 0)`
        : `translate3d(${-this.currentTranslate}px, 0, 0)`;

    // Update progress ratio
    this.style.setProperty(
      "--change-ratio",
      `${this.currentTranslate / this.maxTranslateValue}`,
    );
  }

  /**
   * Dispatch slider change event
   */
  dispatchChangeEvent() {
    this.dispatchEvent(
      new CustomEvent("sliderChanged", {
        bubbles: true,
        detail: {
          currentIndex: this.index,
          currentElement: this.sliderItems[this.index],
        },
      }),
    );
  }

  // Correct index bounds
  correctIndex() {
    if (this.ifEnableLooping) {
      if (this.index < 0) {
        this.index = this.slideLength - this.slidesPerPage;
      } else if (this.index > this.slideLength - this.slidesPerPage) {
        this.index = 0;
      }
    } else {
      if (this.index < 0) {
        this.index = 0;
      } else if (this.index > this.slideLength - this.slidesPerPage) {
        this.index = this.slideLength - this.slidesPerPage;
      }
    }
  }

  /**
   * Correct translate value within bounds
   */
  correctTranslate() {
    // Drag threshold for elastic effect, only active during drag
    let threshold = 0;
    if (this.dragHandler && this.dragHandler.isDragging)
      threshold = Math.min(
        this.sliderItemOffset / 2,
        this.TRANSITION_THRESHOLD,
      );
    const boundary = [-threshold, this.maxTranslateValue + threshold];

    if (this.currentTranslate > boundary[1]) {
      this.currentTranslate = boundary[1];
      this.setAttribute("data-move-exceed", "next");
    } else if (this.currentTranslate < boundary[0]) {
      this.currentTranslate = boundary[0];
      this.setAttribute("data-move-exceed", "pre");
    } else {
      this.removeAttribute("data-move-exceed");
    }
  }

  /**
   * Update slider state info
   */
  updateInfo() {
    // Activate the current slide item
    const activeSlide = this.currentSlide;
    this.currentSlide = this.sliderItems[this.index];

    if (activeSlide) {
      activeSlide.classList.remove("active");
      if (this.ifAriaLive) activeSlide.setAttribute("aria-hidden", "true"); // If aria-live enabled, only show active item
    }
    if (this.currentSlide) {
      this.currentSlide.classList.add("active");
      if (this.ifAriaLive) this.currentSlide.removeAttribute("aria-hidden"); // If aria-live enabled, only show active item
    }

    // Pause all playing media
    if (!this.keepVideo) {
      webvista.pauseAllMedia(this);

      // Delay to ensure video is fully initialized
      setTimeout(() => {
        webvista.playAllMedia(this.currentSlide);
      }, 50);
    }

    // Dispatch sliderChanged event
    setTimeout(this.dispatchChangeEvent.bind(this), this.TRANSITION_DURATION);

    if (this.autoplayHandler && !this.autoplayHandler.userPaused) this.autoplayHandler.play(true); // Resume autoplay

    // Update basic module info
    this.basicHandler.updateInfo(this.currentSlide, this.totalPages);
  }

  /**
   * Check if an element is currently visible in the viewport
   * @param element
   * @returns {boolean}
   */
  isVisibleSlide(element = null) {
    if (!element) return false;

    const elementIndex = this.sliderItems.indexOf(element);
    // Return false if element is not in sliderItems
    if (elementIndex === -1) return false;

    return (
      elementIndex >= this.index &&
      elementIndex < this.index + this.slidesPerPage
    );
  }

  /**
   * Check if an element is the current active slide
   * @param element
   * @returns {boolean}
   */
  isCurrentSlide(element = null) {
    return element === this.sliderItems[this.index];
  }

  // Disable transition
  disableTransition() {
    this.sliderWrapper.style.transition = "";
  }

  // Enable transition
  enableTransition() {
    if (this.ifSlideSmooth)
      this.sliderWrapper.style.transition = `transform ${this.TRANSITION_DURATION}ms`;
  }

  /**
   * Clear slider translation
   */
  clearTranslation() {
    this.index = 0;
    this.preTranslate = 0;
    this.currentTranslate = 0;
    this.sliderWrapper.style.transition = "";
    this.sliderWrapper.style.transform = "";
  }
}

customElements.define("slider-component", Slider);

/**
 * Product detail media slider
 */
class ProductMediaSlider extends Slider {}

customElements.define("product-media-slider", ProductMediaSlider);

/**
 * Product thumbnail slider
 */
class ProductThumbnailSlider extends Slider {
  constructor() {
    super();
  }

  /**
   * Move thumbnail element to the optimal visible position
   * @param element
   * @returns {boolean}
   */
  moveElementToOptimalPosition(element) {
    if (!this.initSliderStatus || !element) return false;

    const elementIndex = this.sliderItems.indexOf(element);
    if (elementIndex < 0) return false;

    this.index = elementIndex;
    // Adjust index to an optimal position
    if (this.index > this.slideLength - this.slidesPerPage) {
      this.index = this.totalPages - 1; // Last page
    } else if (this.index > 0) {
      this.index = this.index - 1;
    }

    this.performSlide();
  }
}
customElements.define("product-thumbnail-slider", ProductThumbnailSlider);
